import dayjs from "dayjs";
import { UUID } from "crypto";
import utc from "dayjs/plugin/utc";

import { CatalogFile, DocumentConfig } from "@interfaces/catalogs";
import { ContractualDocWithCfg, DocumentInfo, DocumentInfoWithCfg } from "@interfaces/documents";

dayjs.extend(utc);

export const generateDummyContractDoc = (
    total: number,
    category: CatalogFile,
    config: DocumentConfig,
    childCodes: Array<string>,
    person_id?: UUID,
): Array<ContractualDocWithCfg> => {
    const tmp: Array<ContractualDocWithCfg> = [];

    for (let i = 0; i < total; i++) {
        tmp.push({
            file_category_id: category?.id ?? "0-0-0-0-0",
            client_details_id: "0-0-0-0-0",
            loan_application_id: "0-0-0-0-0",
            loan_id: "0-0-0-0-0",
            url: "",
            validated: null,
            sign_client: null,
            sign_user: null,
            sign_validated: null,
            sign_date: "",
            file_name: "",
            created_at: "",
            updated_at: "",
            active: false,
            sign_document_reference_id: "0-0-0-0-0",
            signers_reference: null,
            sign_service_name: null,
            id: "0-0-0-0-0",
            file_category_code: category?.code ?? "",
            file_category: category,
            person_id: person_id ?? "0-0-0-0-0",
            guarantee_id: "0-0-0-0-0",
            config: config,
            childCodes: childCodes,
        });
    }

    return tmp;
};

export const generateDummyDocuments = (
    total: number,
    values: Partial<DocumentInfo>,
    extras: Partial<Omit<DocumentInfoWithCfg, keyof DocumentInfo>>,
): Array<DocumentInfoWithCfg> => {
    return Array.from({ length: total }, (_, i) => {
        return {
            id: "0-0-0-0-0",
            loan_application_id: "0-0-0-0-0",
            file_category_id: "0-0-0-0-0",
            file_name: "",
            url: "",
            status: false,
            validated: undefined,
            sign_client: undefined,
            sign_user: undefined,
            sign_validated: undefined,
            created_at: "",
            updated_at: "",
            file_code: "",
            client_details_id: "0-0-0-0-0",
            person_id: "0-0-0-0-0",
            guarantee_id: "0-0-0-0-0",
            ...values,
            order: i + 1,
            upload_type: "client",
            person_type: undefined,
            accountId: "0-0-0-0-0",
            ...extras,
        };
    });
};

export const calculateDatetimeDiff = (dt: string): string => {
    const t1 = dayjs.utc(dt);
    const t2 = dayjs();

    const minutesDiff = t2.diff(t1, "minute");
    const hoursDiff = t2.diff(t1, "hour");
    const daysDiff = t2.diff(t1, "day");

    let tmp: string = "";
    if (minutesDiff !== 0) {
        tmp = `Hace ${minutesDiff} minutos`;
    }

    if (hoursDiff !== 0) {
        tmp = `Hace ${hoursDiff} horas`;
    }

    if (daysDiff !== 0) {
        tmp = `Hace ${daysDiff} días`;
    }

    return tmp;
};

export const getMimeTypeFromFilename = (filename: string): string => {
    let extension = filename.split(".").pop();

    return (
        {
            aac: "audio/aac",
            abw: "application/x-abiword",
            arc: "application/x-freearc",
            avi: "video/x-msvideo",
            azw: "application/vnd.amazon.ebook",
            bin: "application/octet-stream",
            bmp: "image/bmp",
            bz: "application/x-bzip",
            bz2: "application/x-bzip2",
            cda: "application/x-cdf",
            csh: "application/x-csh",
            css: "text/css",
            csv: "text/csv",
            doc: "application/msword",
            docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            eot: "application/vnd.ms-fontobject",
            epub: "application/epub+zip",
            gz: "application/gzip",
            gif: "image/gif",
            htm: "text/html",
            html: "text/html",
            ico: "image/vnd.microsoft.icon",
            ics: "text/calendar",
            jar: "application/java-archive",
            jpeg: "image/jpeg",
            jpg: "image/jpeg",
            js: "text/javascript",
            json: "application/json",
            jsonld: "application/ld+json",
            mid: "audio/midi audio/x-midi",
            midi: "audio/midi audio/x-midi",
            mjs: "text/javascript",
            mp3: "audio/mpeg",
            mp4: "video/mp4",
            mpeg: "video/mpeg",
            mpkg: "application/vnd.apple.installer+xml",
            odp: "application/vnd.oasis.opendocument.presentation",
            ods: "application/vnd.oasis.opendocument.spreadsheet",
            odt: "application/vnd.oasis.opendocument.text",
            oga: "audio/ogg",
            ogv: "video/ogg",
            ogx: "application/ogg",
            opus: "audio/opus",
            otf: "font/otf",
            png: "image/png",
            pdf: "application/pdf",
            php: "application/x-httpd-php",
            ppt: "application/vnd.ms-powerpoint",
            pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
            rar: "application/vnd.rar",
            rtf: "application/rtf",
            sh: "application/x-sh",
            svg: "image/svg+xml",
            swf: "application/x-shockwave-flash",
            tar: "application/x-tar",
            tif: "image/tiff",
            tiff: "image/tiff",
            ts: "video/mp2t",
            ttf: "font/ttf",
            txt: "text/plain",
            vsd: "application/vnd.visio",
            wav: "audio/wav",
            weba: "audio/webm",
            webm: "video/webm",
            webp: "image/webp",
            woff: "font/woff",
            woff2: "font/woff2",
            xhtml: "application/xhtml+xml",
            xls: "application/vnd.ms-excel",
            xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            xml: "application/xml",
            xul: "application/vnd.mozilla.xul+xml",
            zip: "application/zip",
            "3gp": "video/3gpp",
            "3g2": "video/3gpp2",
            "7z": "application/x-7z-compressed",
        }[extension] || "application/octet-stream"
    );
};

export const conversionToTreeViewItem = (item: any, lvl: number) => {
    const years = Object.keys(item).filter((key) => isNaN(parseInt(key)) == false);

    const tmp = {
        id: crypto.randomUUID(),
        label: item.category,
        children: item.children?.map((child: any) => conversionToTreeViewItem(child, lvl + 1)),
        lvl: lvl,
    };

    years.map((year) => (tmp[year] = item[year].Total ?? 0));

    return tmp;
};

export const inputOnlyAlpha = (event: any, formik: any) => {
    if (/^[a-zA-ZáéíóúñüÑÁÉÍÓÚ\s]*$/.test(event.target.value)) {
        formik.handleChange(event);
    }
};

export const inputConvertToUppercase = (event: any, formik: any) => {
    const modified = {
        ...event,
        target: {
            ...event.target,
            id: event.target.id,
            name: event.target.name,
            value: event.target.value.toUpperCase(),
        },
    };

    formik.handleChange(modified);
};

export const inputConvertToLowercase = (event: any, formik: any) => {
    const modified = {
        ...event,
        target: {
            ...event.target,
            id: event.target.id,
            name: event.target.name,
            value: event.target.value.toLowerCase(),
        },
    };

    formik.handleChange(modified);
};

export const pick = <T, K extends keyof T>(obj: T, keys: K[]): Pick<T, K> => {
    const result = {} as Pick<T, K>;
    for (const key of keys) {
        result[key] = obj[key];
    }

    return result;
};

export const omit = <T, K extends keyof T>(obj: T, keys: K[]): Omit<T, K> => {
    const result = {} as Omit<T, K>;
    for (const key in obj) {
        if (!keys.includes(key as K)) {
            result[key] = obj[key];
        }
    }
    return result;
};

export const normalizeExtension = (filename: string): string => {
    const parts = filename.split(".");
    if (parts.length < 2) return filename;

    const extension = parts.pop()?.toLowerCase();
    const name = parts.join(".");

    return `${name}.${extension}`;
};

export const hasAllData = <T extends object>(obj: T): boolean => {
    return Object.values(obj).every((value) => value !== null && value !== undefined);
};

type NestedCodeItem = { code: string };

export const filterByNestedCodes = <T>(
    data: T[],
    getNestedArray: (item: T) => NestedCodeItem[],
    allowedCodes: string[],
): T[] => {
    const allowedSet = new Set(allowedCodes);
    return data.filter((item) => getNestedArray(item).some((detail) => allowedSet.has(detail.code)));
};

export const filterByExcludedCodes = <T>(
    data: T[],
    getNestedArray: (item: T) => NestedCodeItem[],
    excludedCodes: string[],
): T[] => {
    const excludedSet = new Set(excludedCodes);
    return data.filter((item) => getNestedArray(item).every((detail) => !excludedSet.has(detail.code)));
};

export const validateRFC = (rfc: string) => {
    const RFC_GENERICOS = ["XAXX010101", "XEXX010101"];
    const rfcNormalizado = rfc.trim().toUpperCase();

    if (RFC_GENERICOS.some((t) => rfcNormalizado.startsWith(t) === true)) {
        return true;
    }

    const regexRFC = /^([A-ZÑ&]{3,4})(\d{2})(\d{2})(\d{2})([A-Z\d]{3})$/;
    if (!regexRFC.test(rfcNormalizado)) {
        return false;
    }

    const [, , yy, mm, dd] = regexRFC.exec(rfcNormalizado)!;
    // const year = parseInt(yy, 10);
    const month = parseInt(mm, 10);
    const day = parseInt(dd, 10);

    if (month < 1 || month > 12) return false;
    if (day < 1 || day > 31) return false;

    return true;
};

export const getCreditClasification = (creditTypeCode: number): "FINANCIAL" | "COMMERCIAL" => {
    if (
        (creditTypeCode >= 1300 && creditTypeCode <= 1399) ||
        (creditTypeCode >= 1400 && creditTypeCode <= 1499) ||
        (creditTypeCode >= 1500 && creditTypeCode <= 1599) ||
        (creditTypeCode >= 2000 && creditTypeCode <= 2999) ||
        (creditTypeCode >= 3000 && creditTypeCode <= 3999) ||
        (creditTypeCode >= 4000 && creditTypeCode <= 4999)
    ) {
        return "FINANCIAL";
    } else {
        return "COMMERCIAL";
    }
};

export const deepEqual = (a: any, b: any) => {
    if (a === b) return true;

    if (typeof a !== "object" || typeof b !== "object" || a == null || b == null) {
        return false;
    }

    const keysA = Object.keys(a);
    const keysB = Object.keys(b);

    if (keysA.length !== keysB.length) return false;

    for (let key of keysA) {
        if (!keysB.includes(key) || !deepEqual(a[key], b[key])) {
            return false;
        }
    }

    return true;
};

export const calculateWeeklyPayment = (creditAmount: number, weekly_fee: number, authorized_period: number) => {
    if (weekly_fee === 0) {
        return creditAmount / authorized_period;
    }

    weekly_fee = weekly_fee / 100;
    const payment = (weekly_fee * creditAmount) / (1 - Math.pow(1 + weekly_fee, -authorized_period));

    return payment;
};

export const formatFullName = (person?: { name?: string; last_name?: string; last_name_2?: string }) => {
    return [person?.name, person?.last_name, person?.last_name_2].filter(Boolean).join(" ");
};
